import { clsx, isKeyboardClick, mergeRefs } from "@regrello/core-utils";
import { useState } from "react";
import { buttonPressedVariants, buttonVariants } from "../regrelloButtonUtils";
import type {
  RegrelloAnchorButtonProps,
  RegrelloButtonProps,
  RegrelloButtonSharedProps,
  RegrelloLinkButtonProps,
} from "../types";
import { RegrelloButtonContents } from "./RegrelloButtonContents";

/**
 * Hook that returns native HTML props for use on the root tag of all `Regrello*Button`
 * implementations. Ensures consistent styling and accessibility.
 */
export function getButtonProps<
  E extends HTMLAnchorElement | HTMLButtonElement,
  P extends E extends HTMLAnchorElement ? RegrelloAnchorButtonProps : RegrelloButtonProps,
>(rawProps: P, ref: React.Ref<E>): P {
  const props = populatePropDefaults(rawProps);

  const className = clsx(
    buttonVariants({
      active: props.active,
      iconOnly: props.iconOnly ? props.size : undefined,
      intent: props.intent,
      shape: props.shape,
      size: props.iconOnly ? undefined : props.size,
      variant: props.variant,
    }),
    {
      "w-full": props.fullWidth,
      "text-transparent": props.loading,
      "justify-between": props.justification === "stretch" && !props.loading,
    },
    props.className,
  );

  const isDisabled = props.disabled || props.loading;

  return {
    ...props,
    className: clsx(className, props.className),
    "data-testid": props.dataTestId,
    disabled: isDisabled,
    ref: mergeRefs(ref, props.ref as React.Ref<E>),
  };
}

/**
 * Hook that returns native HTML props for use on the root tag of all `<a>`-backed `Regrello*Button`
 * implementations, ensuring correct handling of "keyboard clicking" (i.e., pressing Space or Enter
 * to click the anchor button).
 *
 * @param props - The props that were passed into the button component.
 * @param ref - A ref to the anchor element.
 * @returns The props that should be spread onto the root element of the anchor button (note that
 * you must filter through `retainValidHTMLProps` first).
 */
export function useAnchorButtonInteractiveProps(
  props: RegrelloAnchorButtonProps | RegrelloLinkButtonProps,
  ref: React.RefObject<HTMLAnchorElement | null>,
): RegrelloAnchorButtonProps | RegrelloLinkButtonProps {
  const [isPressed, setIsPressed] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // (dkim): Passing `disabled={true}` to links is not supported, and passing
    // `aria-disabled={true}` is semantic rather than functional. We need to explicitly
    // swallow the click event here in order to fully disable the link button.

    if (props.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    props.onClick?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (isKeyboardClick(event)) {
      // Prevent scrolling the page.
      event.preventDefault();

      if (!props.disabled) {
        setIsPressed(true);
      }
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (isKeyboardClick(event)) {
      if (!props.disabled) {
        setIsPressed(false);
        ref.current?.click();
      }
    }
  };

  return {
    ...props,
    "aria-disabled": props.disabled,
    className: clsx(
      props.className,
      buttonPressedVariants({
        intent: props.intent,
        pressed: isPressed,
        variant: props.variant,
      }),
    ),
    href: props.disabled ? undefined : props.href,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyUp,
    tabIndex: props.disabled ? -1 : 0,
  };
}

/**
 * Filters out Regrello-specific props from the given props object, returning only the native HTML
 * props that should be spread onto the root element of the button.
 *
 * @param props - The props that were passed into the button component.
 * @returns The native HTML props that should be spread onto the root element of the button.
 */
export function retainValidHTMLProps<T extends HTMLElement>(props: RegrelloButtonSharedProps): React.HTMLProps<T> {
  // Extract Regrello-specific props to avoid spreading them invalidly to a native HTML element
  // (doing so would result in console errors).
  const {
    allowWrap,
    contentClassName,
    dataTestId,
    endIcon,
    fullWidth,
    iconOnly,
    intent,
    justification,
    loading,
    shape,
    size,
    startIcon,
    variant,
    ...htmlProps
  } = props;
  return htmlProps;
}

/**
 * Renders the inner button contents. Ensures that we don't forget to pass in any optional props.
 *
 * @param props - The props that were passed into the button component.
 * @returns The button contents as a React node.
 */
export function renderButtonContents(props: RegrelloButtonSharedProps): React.ReactNode {
  return (
    <RegrelloButtonContents
      allowWrap={props.allowWrap}
      contentClassName={props.contentClassName}
      disabled={props.disabled}
      endIcon={props.endIcon}
      fullWidth={props.fullWidth}
      iconOnly={props.iconOnly}
      intent={props.intent}
      justification={props.justification}
      loading={props.loading}
      shape={props.shape}
      size={props.size}
      startIcon={props.startIcon}
      variant={props.variant}
    >
      {props.children}
    </RegrelloButtonContents>
  );
}

/**
 * Populates default values for all button props. This allows us to have one place where we define
 * these rather than in every button component.
 *
 * @param props - The props that were passed into the button component.
 * @returns The props with default values defined.
 */
function populatePropDefaults<P extends RegrelloButtonSharedProps>(props: P): P {
  return {
    ...props,
    allowWrap: props.allowWrap ?? false,
    intent: props.intent ?? "neutral",
    justification: props.justification ?? "center",
    loading: props.loading ?? false,
    shape: props.shape ?? "rectangle",
    size: props.size ?? "large",
    variant: props.variant ?? "solid",
  };
}
